import React from 'react';
import { Card, Typography, Alert, Spin } from 'antd';
import { ApartmentOutlined, InfoCircleOutlined } from '@ant-design/icons';
import CMTStructureVisualization from '../shared/CMTStructureVisualization';
import useCMTStructure from '../../hooks/useCMTStructure';

const { Title, Text } = Typography;

/**
 * 🏢 CMT Structure Tab Component
 * 
 * Displays the CMT organizational structure for a specific organization
 * Used in the Admin > Client Organisations > View Organisation modal
 * 
 * @param {string} orgId - Organization ID to display CMT structure for
 */
const CMTStructureTab = ({ orgId }) => {
  const { hierarchyData, loading, error, reload } = useCMTStructure(orgId);

  if (!orgId) {
    return (
      <Alert
        message="No Organization Selected"
        description="Please select an organization to view its CMT structure."
        type="warning"
        showIcon
        icon={<InfoCircleOutlined />}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" />
        <Text style={{ marginLeft: '16px', fontSize: '16px' }}>
          Loading CMT structure...
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading CMT Structure"
        description={`Failed to load CMT data: ${error}`}
        type="error"
        showIcon
        action={
          <Text 
            link 
            onClick={reload}
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            Try Again
          </Text>
        }
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
          <ApartmentOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          Crisis Management Team Structure
        </Title>
        <Text type="secondary" style={{ fontSize: '14px' }}>
          Organizational hierarchy and reporting relationships for crisis management teams
        </Text>
      </div>

      {!hierarchyData ? (
        <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
          <ApartmentOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
          <Title level={4} type="secondary">No CMT Structure Configured</Title>
          <Text type="secondary">
            This organization has not configured a crisis management team structure yet.
            <br />
            Use the onboarding wizard to set up their CMT hierarchy.
          </Text>
        </Card>
      ) : (
        <Card style={{ padding: '0', overflow: 'hidden' }}>
          <CMTStructureVisualization 
            hierarchyData={hierarchyData}
            readOnly={true}
          />
        </Card>
      )}

      {hierarchyData && (
        <div style={{ marginTop: '16px' }}>
          <Alert
            message="Future Enhancement"
            description="Editing capabilities will be added in a future release. This view is currently read-only for validation purposes."
            type="info"
            showIcon
            closable
          />
        </div>
      )}
    </div>
  );
};

export default CMTStructureTab;